import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AlignCenter, AlignLeft, AlignRight, Baseline, Bold, Italic, Trash2 } from "@fg/icons";
import { cn } from "@/lib/utils";
import type { FreeTextStyle } from "./editTypes";
import { showFont } from "./fonts";
import { FontPicker } from "./FontPicker";

const SPACINGS = [1, 1.15, 1.25, 1.5, 2, 2.5];
const SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 72];

/**
 * The floating bar over the selected text box in Edit mode: font, size,
 * bold, colour, remove. It follows the box through scroll, zoom and moves
 * (one getBoundingClientRect per frame while shown) and flips below the box
 * when there's no room above it.
 */
export function FreeTextMiniToolbar({
    getRect, style, onChange, onRemove
}: {
    /** The text box on screen, or null when it isn't. */
    getRect: () => DOMRect | null;
    style: FreeTextStyle;
    onChange: (patch: Partial<FreeTextStyle>) => void;
    onRemove: () => void;
}) {
    const ref = useRef<HTMLDivElement>(null);
    const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
    useEffect(() => { void showFont(style.fontFamily); }, [style.fontFamily]);
    const chooseFont = (value: string) => {
        void showFont(value);
        onChange({ fontFamily: value });
    };

    useEffect(() => {
        let frame = 0;
        let last = "";
        const tick = () => {
            const r = getRect();
            const el = ref.current;
            let next: { left: number; top: number } | null = null;
            if (r && r.width > 0) {
                const area = document.getElementById("container_viewerContainer")?.getBoundingClientRect();
                const visible = !area || (r.bottom > area.top && r.top < area.bottom);
                if (visible) {
                    const h = el?.offsetHeight ?? 36;
                    const w = el?.offsetWidth ?? 300;
                    let top = r.top - h - 8;
                    if (top < (area?.top ?? 0) + 4) top = r.bottom + 8;
                    const left = Math.min(Math.max(8, r.left - 2), window.innerWidth - w - 8);
                    next = { left, top };
                }
            }
            const key = next ? `${Math.round(next.left)},${Math.round(next.top)}` : "";
            if (key !== last) {
                last = key;
                setPos(next);
            }
            frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [getRect]);

    // The colour picker reports every drag step — apply once it settles.
    const colourTimer = useRef<number | undefined>(undefined);
    const [colour, setColour] = useState(() => toHex(style.fontColor));
    useEffect(() => setColour(toHex(style.fontColor)), [style.fontColor]);
    useEffect(() => () => window.clearTimeout(colourTimer.current), []);
    const pickColour = (value: string) => {
        setColour(value);
        window.clearTimeout(colourTimer.current);
        colourTimer.current = window.setTimeout(() => onChange({ fontColor: value }), 250);
    };

    const size = Math.round(style.fontSize);
    const sizes = SIZES.includes(size) ? SIZES : [...SIZES, size].sort((a, b) => a - b);
    const control = "h-7 rounded border bg-background px-1.5 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
    const iconButton = "flex h-7 w-7 items-center justify-center rounded hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

    return createPortal(
        <div
            ref={ref}
            role="toolbar"
            aria-label="Text formatting"
            style={pos ? { left: pos.left, top: pos.top } : { left: 0, top: 0, visibility: "hidden" }}
            className="fixed z-50 flex items-center gap-0.5 whitespace-nowrap rounded-md border bg-card p-1 text-xs text-card-foreground shadow-lg"
        >
            <FontPicker value={style.fontFamily} onPick={chooseFont} className="w-36" />
            <select aria-label="Font size" value={String(size)} onChange={(e) => onChange({ fontSize: Number(e.target.value) })} className={cn(control, "w-14 tabular-nums")}>
                {sizes.map((s) => (
                    <option key={s} value={s}>{s}</option>
                ))}
            </select>
            <button
                type="button"
                aria-label="Bold"
                aria-pressed={style.bold}
                title="Bold"
                onClick={() => onChange({ bold: !style.bold })}
                className={cn(iconButton, style.bold && "bg-primary/10 text-primary")}
            >
                <Bold className="h-4 w-4" aria-hidden />
            </button>
            <button type="button" aria-label="Italic" aria-pressed={style.italic} title="Italic" onClick={() => onChange({ italic: !style.italic })} className={cn(iconButton, style.italic && "bg-primary/10 text-primary")}>
                <Italic className="h-4 w-4" aria-hidden />
            </button>
            <label title="Text colour" className={cn(iconButton, "relative cursor-pointer focus-within:ring-2 focus-within:ring-ring")}>
                <Baseline className="h-4 w-4" aria-hidden />
                <span className="absolute inset-x-1.5 bottom-1 h-0.5 rounded" style={{ background: colour }} aria-hidden />
                <input
                    type="color"
                    aria-label="Text colour"
                    value={colour}
                    onChange={(e) => pickColour(e.target.value)}
                    className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                />
            </label>
            <span className="mx-0.5 h-5 w-px bg-border" aria-hidden />
            {([["left", "Align left", AlignLeft], ["center", "Centre", AlignCenter], ["right", "Align right", AlignRight]] as const).map(([a, label, Icon]) => (
                <button key={a} type="button" aria-label={label} title={label} aria-pressed={style.align === a} onClick={() => onChange({ align: a })} className={cn(iconButton, style.align === a && "bg-primary/10 text-primary")}>
                    <Icon className="h-4 w-4" aria-hidden />
                </button>
            ))}
            <select aria-label="Line spacing" title="Line spacing" value={String(SPACINGS.includes(Math.round(style.lineSpacing * 100) / 100) ? Math.round(style.lineSpacing * 100) / 100 : "custom")} onChange={(e) => { if (e.target.value !== "custom") onChange({ lineSpacing: Number(e.target.value) }); }} className={cn(control, "w-16 tabular-nums")}>
                {!SPACINGS.includes(Math.round(style.lineSpacing * 100) / 100) && <option value="custom">{style.lineSpacing.toFixed(2)}</option>}
                {SPACINGS.map((v) => (
                    <option key={v} value={v}>{v.toFixed(2)}</option>
                ))}
            </select>
            <span className="mx-0.5 h-5 w-px bg-border" aria-hidden />
            <button type="button" aria-label="Remove this text" title="Remove (the original shows again if this was an Edit text)" onClick={onRemove} className={iconButton}>
                <Trash2 className="h-4 w-4" aria-hidden />
            </button>
        </div>,
        document.body
    );
}

/** `<input type=color>` only takes #rrggbb. */
function toHex(colour: string): string {
    const c = (colour ?? "").trim();
    if (/^#[0-9a-f]{6}$/i.test(c)) return c.toLowerCase();
    const short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(c);
    if (short) return `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`.toLowerCase();
    const rgb = /^rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(c);
    if (rgb) return `#${[rgb[1], rgb[2], rgb[3]].map((n) => Math.min(255, Number(n)).toString(16).padStart(2, "0")).join("")}`;
    return "#000000";
}
